import type { PrismaClient, PurchaseOrder, SalesDocument } from '@prisma/client';
import { formatMoney, normalisePhone, whatsappLink } from '@roovers/shared';
import { z } from 'zod';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { permissionsGranted } from '../auth/auth.middleware.js';
import type { AuthContext } from '../auth/auth.types.js';
import {
  assertSafeHeader,
  assertValidEmail,
  queueEmail,
  renderTemplate,
} from '../messaging/messaging.service.js';
import {
  DOC_LABEL,
  DOC_PERM,
  approveDocument,
  assertWithinLimit,
  isoOf,
  kindOf,
  loadDocument,
  logEvent,
  type DocKind,
} from './documents.service.js';
import { sendWhatsAppCloudText, whatsappCloudEnabled } from '../messaging/whatsapp.cloud.js';
import { scheduleDocumentReminders } from './reminders.service.js';
import { createShareLink, shareUrl } from './share.service.js';

export type SendKind = DocKind | 'receipt' | 'purchaseorder' | 'payout';

interface Target {
  kind: SendKind;
  id: number;
  companyId: number;
  partyId: number;
  number: string | null;
  currency: string;
  total: string;
  dueDate: string | null;
  validUntil: string | null;
  status: string;
  entityType: 'sales_document' | 'receipt' | 'purchase_order' | 'payout';
  doc?: SalesDocument;
  order?: PurchaseOrder;
}

async function loadTarget(
  db: PrismaClient,
  auth: AuthContext,
  kind: SendKind,
  id: number,
): Promise<Target> {
  if (kind === 'receipt') {
    const r = await db.paymentReceipt.findFirst({
      where: {
        id,
        companyId: auth.companyId,
        ...(auth.scope === 'own' ? { createdBy: auth.userId } : {}),
      },
    });
    if (!r) throw new HttpError(404, 'NOT_FOUND', 'Receipt not found');
    return {
      kind,
      id,
      companyId: r.companyId,
      partyId: r.partyId,
      number: r.receiptNo,
      currency: r.currency,
      total: r.amount.toString(),
      dueDate: null,
      validUntil: null,
      status: r.status,
      entityType: 'receipt',
    };
  }
  if (kind === 'purchaseorder') {
    const o = await db.purchaseOrder.findFirst({
      where: {
        id,
        companyId: auth.companyId,
        ...(auth.scope === 'own' ? { ownerId: auth.userId } : {}),
      },
    });
    if (!o) throw new HttpError(404, 'NOT_FOUND', 'Purchase order not found');
    return {
      kind,
      id,
      companyId: o.companyId,
      partyId: o.partyId,
      number: o.number,
      currency: o.currency,
      total: o.total.toString(),
      dueDate: null,
      validUntil: null,
      status: o.status,
      entityType: 'purchase_order',
      order: o,
    };
  }
  if (kind === 'payout') {
    const p = await db.payoutReceipt.findFirst({
      where: {
        id,
        companyId: auth.companyId,
        ...(auth.scope === 'own' ? { createdBy: auth.userId } : {}),
      },
    });
    if (!p) throw new HttpError(404, 'NOT_FOUND', 'Payout not found');
    return {
      kind,
      id,
      companyId: p.companyId,
      partyId: p.partyId,
      number: p.payoutNo,
      currency: p.currency,
      total: p.amount.toString(),
      dueDate: null,
      validUntil: null,
      status: p.status,
      entityType: 'payout',
    };
  }
  const doc = await loadDocument(db, auth, id);
  if (kindOf(doc) !== kind) throw new HttpError(404, 'NOT_FOUND', 'Document not found');
  return {
    kind,
    id,
    companyId: doc.companyId,
    partyId: doc.partyId,
    number: doc.number,
    currency: doc.currency,
    total: doc.total.toString(),
    dueDate: isoOf(doc.dueDate),
    validUntil: isoOf(doc.validUntil),
    status: doc.status,
    entityType: 'sales_document',
    doc,
  };
}

const permFor = (kind: SendKind) =>
  kind === 'receipt'
    ? 'sales.receipt'
    : kind === 'purchaseorder'
      ? 'purchases.order'
      : kind === 'payout'
        ? 'purchases.payout'
        : DOC_PERM[kind];
const labelFor = (kind: SendKind) =>
  kind === 'receipt'
    ? 'Payment receipt'
    : kind === 'purchaseorder'
      ? 'Purchase order'
      : kind === 'payout'
        ? 'Payout receipt'
        : DOC_LABEL[kind];
const templateType = (kind: SendKind) => (kind === 'creditnote' ? 'invoice' : kind);

export function assertCanSend(auth: AuthContext, kind: SendKind) {
  if (!permissionsGranted(auth.permissions, `${permFor(kind)}.send`))
    throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to send this');
}

/** Whether the document can go out now: issued, or the sender is allowed to approve it on the spot. */
function approvalState(auth: AuthContext, t: Target): 'ready' | 'auto_approve' | 'needs_approval' {
  if (t.kind === 'receipt' || t.kind === 'payout')
    return t.status === 'void' ? 'needs_approval' : 'ready';
  if (['draft', 'pending_approval'].includes(t.status)) {
    const canApprove = permissionsGranted(auth.permissions, `${permFor(t.kind)}.approve`);
    const base = t.doc ? t.doc.baseTotal : t.order!.baseTotal;
    const within = auth.approvalLimit === null || Number(base.toString()) <= auth.approvalLimit;
    return canApprove && within ? 'auto_approve' : 'needs_approval';
  }
  return ['void', 'declined', 'expired', 'converted', 'cancelled', 'closed'].includes(t.status)
    ? 'needs_approval'
    : 'ready';
}

async function templateFor(
  db: PrismaClient,
  companyId: number,
  docType: string,
  channel: 'email' | 'whatsapp',
  name = 'default',
) {
  return db.messageTemplate.findFirst({ where: { companyId, docType, channel, name } });
}

async function variables(db: PrismaClient, auth: AuthContext, t: Target) {
  const party = await db.party.findUnique({
    where: { id: t.partyId },
    include: { contacts: true },
  });
  const company = await db.company.findUniqueOrThrow({ where: { id: t.companyId } });
  return {
    party,
    company,
    vars: {
      client_name: party?.name ?? '',
      document_number: t.number ?? '',
      total: formatMoney(t.total, t.currency),
      due_date: t.dueDate ? t.dueDate.split('-').reverse().join('/') : '',
      valid_until: t.validUntil ? t.validUntil.split('-').reverse().join('/') : '',
      company_name: company.name,
      sender_name: auth.name,
    },
  };
}

// ───────────── preview (what the Send dialog shows) ─────────────

export async function sendPreview(db: PrismaClient, auth: AuthContext, kind: SendKind, id: number) {
  const t = await loadTarget(db, auth, kind, id);
  const { party, vars } = await variables(db, auth, t);
  const emailTpl = await templateFor(db, t.companyId, templateType(kind), 'email');
  const waTpl = await templateFor(db, t.companyId, templateType(kind), 'whatsapp');
  const label = labelFor(kind);
  // `{{link}}` is kept: the real secure link is created at send time.
  const keepLink = { ...vars, link: '{{link}}' };
  const contacts = (party?.contacts ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    email: c.email,
    phone: c.phone,
    whatsappNumber: c.whatsappNumber,
    receivesDocuments: c.receivesDocuments,
  }));
  let docContacts = contacts.filter((c) => c.receivesDocuments && c.email);
  if (docContacts.length === 0 && (kind === 'purchaseorder' || kind === 'payout'))
    docContacts = contacts.filter((c) => c.email).slice(0, 1);
  const waContact =
    contacts.find((c) => c.receivesDocuments && (c.whatsappNumber || c.phone)) ??
    contacts.find((c) => c.whatsappNumber || c.phone);
  return {
    kind,
    id,
    number: t.number,
    partyId: t.partyId,
    partyName: party?.name ?? '',
    approval: approvalState(auth, t),
    contacts,
    defaultEmails: docContacts.map((c) => c.email!),
    defaultWhatsApp: waContact ? (waContact.whatsappNumber ?? waContact.phone) : null,
    email: {
      subject: renderTemplate(
        emailTpl?.subject ?? `${label} {{document_number}} from {{company_name}}`,
        keepLink,
      ),
      body: renderTemplate(
        emailTpl?.body ??
          `Dear {{client_name}},\n\nPlease find ${label.toLowerCase()} {{document_number}} for {{total}}: {{link}}\n\nKind regards,\n{{sender_name}}\n{{company_name}}`,
        keepLink,
      ),
    },
    whatsapp: {
      body: renderTemplate(
        waTpl?.body ??
          `Hello {{client_name}}, ${label.toLowerCase()} {{document_number}} ({{total}}) from {{company_name}}: {{link}}`,
        keepLink,
      ),
    },
    limits: {
      whatsapp:
        'WhatsApp opens with your message pre-filled and you press Send. The PDF cannot be attached (the client gets a secure link to view and download it) and delivery cannot be confirmed.',
    },
  };
}

// ───────────── sending ─────────────

export const sendInputSchema = z.object({
  channels: z
    .array(z.enum(['email', 'whatsapp']))
    .min(1)
    .max(2),
  email: z
    .object({
      to: z.array(z.string()).max(20).default([]),
      cc: z.array(z.string()).max(20).default([]),
      bcc: z.array(z.string()).max(20).default([]),
      subject: z.string().trim().min(1).max(300),
      message: z.string().min(1).max(8000),
      attachPdf: z.boolean().default(true),
    })
    .optional(),
  whatsapp: z
    .object({ to: z.string().trim().min(6).max(30), message: z.string().min(1).max(4000) })
    .optional(),
  includeLink: z.boolean().default(true),
});
export type SendInput = z.infer<typeof sendInputSchema>;

export interface SendResult {
  status: string;
  number: string | null;
  communications: { id: number; channel: string; recipient: string; status: string }[];
  whatsappUrl: string | null;
  shareUrl: string | null;
}

/** Per-user and per-company throttles so a compromised or careless account cannot spam clients. */
async function assertSendRate(db: PrismaClient, auth: AuthContext, count: number) {
  const minute = await db.communication.count({
    where: {
      companyId: auth.companyId,
      sentById: auth.userId,
      createdAt: { gt: new Date(Date.now() - 60_000) },
    },
  });
  if (minute + count > 30)
    throw new HttpError(
      429,
      'RATE_LIMITED',
      'You are sending too fast. Wait a minute and try again.',
    );
  const hour = await db.communication.count({
    where: { companyId: auth.companyId, createdAt: { gt: new Date(Date.now() - 3_600_000) } },
  });
  if (hour + count > 400)
    throw new HttpError(
      429,
      'RATE_LIMITED',
      'The hourly sending limit for the company has been reached.',
    );
}

export async function sendDocument(
  db: PrismaClient,
  config: Pick<Config, 'APP_URL'> &
    Partial<Pick<Config, 'WHATSAPP_CLOUD_TOKEN' | 'WHATSAPP_PHONE_NUMBER_ID'>>,
  auth: AuthContext,
  kind: SendKind,
  id: number,
  rawInput: SendInput,
): Promise<SendResult> {
  assertCanSend(auth, kind);
  const input = sendInputSchema.parse(rawInput);
  let t = await loadTarget(db, auth, kind, id);

  // Documents that need approval cannot be sent until approved.
  const state = approvalState(auth, t);
  if (state === 'needs_approval') {
    throw new HttpError(
      409,
      'NEEDS_APPROVAL',
      t.status === 'void'
        ? 'A void document cannot be sent'
        : 'This document needs approval before it can be sent. Submit it for approval by someone with the right permission.',
    );
  }
  if (state === 'auto_approve' && t.doc) {
    assertWithinLimit(auth, t.doc.baseTotal);
    await db.$transaction((tx) => approveDocument(tx, auth, t.id));
    t = await loadTarget(db, auth, kind, id);
  } else if (state === 'auto_approve' && t.order) {
    assertWithinLimit(auth, t.order.baseTotal);
    const { approveOrder } = await import('../purchases/purchases.service.js');
    await db.$transaction((tx) => approveOrder(tx, auth, t.id));
    t = await loadTarget(db, auth, kind, id);
  }

  // Validate everything BEFORE anything is queued.
  const emails = input.channels.includes('email') ? input.email : undefined;
  if (input.channels.includes('email') && !emails)
    throw new HttpError(400, 'VALIDATION_ERROR', 'Email details are required');
  const recipients = emails
    ? [...emails.to, ...emails.cc, ...emails.bcc].map(assertValidEmail)
    : [];
  if (emails && recipients.length === 0)
    throw new HttpError(400, 'NO_RECIPIENTS', 'Enter at least one email address');
  if (emails) assertSafeHeader(emails.subject, 'Subject');
  let waNumber: string | null = null;
  if (input.channels.includes('whatsapp')) {
    if (!input.whatsapp)
      throw new HttpError(400, 'VALIDATION_ERROR', 'WhatsApp details are required');
    waNumber = normalisePhone(input.whatsapp.to);
    if (!waNumber)
      throw new HttpError(
        400,
        'INVALID_RECIPIENT',
        'Enter a valid WhatsApp number, for example 0772123456',
      );
  }
  await assertSendRate(db, auth, recipients.length + (waNumber ? 1 : 0));

  const { vars } = await variables(db, auth, t);
  let link: string | null = null;
  const message = (text: string) => {
    const rendered = renderTemplate(text, { ...vars, link: link ?? '' });
    return input.includeLink && link && !text.includes('{{link}}')
      ? `${rendered}\n\n${link}`
      : rendered;
  };
  if (
    input.includeLink ||
    `${input.email?.message ?? ''}${input.whatsapp?.message ?? ''}`.includes('{{link}}')
  ) {
    const { token } = await createShareLink(db, {
      companyId: t.companyId,
      entityType: t.entityType,
      entityId: t.id,
      userId: auth.userId,
    });
    link = shareUrl(config.APP_URL, token);
  }

  const templateName = kind === 'receipt' || kind === 'payout' ? 'receipt' : 'document';
  const communications: SendResult['communications'] = [];
  if (emails) {
    const seen = new Set<string>();
    const unique = recipients.filter(
      (r) => !seen.has(r.toLowerCase()) && seen.add(r.toLowerCase()),
    );
    const rows = await queueEmail(db, {
      companyId: t.companyId,
      to: unique,
      subject: renderTemplate(emails.subject, { ...vars, link: link ?? '' }),
      text: message(emails.message),
      docType: kind,
      docId: t.id,
      templateName,
      sentById: auth.userId,
      attachment: emails.attachPdf ? { type: kind, id: t.id } : undefined,
    });
    communications.push(
      ...rows.map((r) => ({
        id: r.id,
        channel: 'email',
        recipient: r.recipient,
        status: r.status,
      })),
    );
  }
  let whatsappUrl: string | null = null;
  if (waNumber && input.whatsapp) {
    const text = message(input.whatsapp.message);
    // With the Cloud API configured we send by API and keep the provider id for delivery receipts;
    // otherwise it is click-to-chat, which cannot confirm delivery (CLAUDE.md rule 10).
    let externalId: string | null = null;
    let apiError: string | null = null;
    if (whatsappCloudEnabled(config)) {
      try {
        externalId = (await sendWhatsAppCloudText(config, waNumber, text)).messageId;
      } catch (e) {
        apiError = (e instanceof Error ? e.message : 'WhatsApp API error').slice(0, 300);
      }
    }
    // On an API failure the person can still send it by hand through the link.
    if (!externalId) whatsappUrl = whatsappLink(waNumber, text);
    const row = await db.communication.create({
      data: {
        companyId: t.companyId,
        docType: kind,
        docId: t.id,
        channel: 'whatsapp',
        recipient: waNumber,
        bodyText: text,
        templateName,
        status: externalId ? 'sent' : 'shared_unconfirmed',
        externalId,
        error: apiError,
        sentById: auth.userId,
        sentAt: new Date(),
      },
    });
    communications.push({
      id: row.id,
      channel: 'whatsapp',
      recipient: waNumber,
      status: row.status,
    });
  }

  // Mark sent, start the reminder clock.
  const now = new Date();
  if (t.doc) {
    const patch: Record<string, unknown> = {};
    if (!t.doc.sentAt) patch.sentAt = now;
    if (t.doc.status === 'approved') patch.status = 'sent';
    if (Object.keys(patch).length)
      await db.salesDocument.update({ where: { id: t.id }, data: patch });
    await logEvent(db, {
      companyId: t.companyId,
      entityType: 'sales_document',
      entityId: t.id,
      type: 'sent',
      userId: auth.userId,
      note: input.channels.join(' + '),
      meta: { recipients: recipients.length, whatsapp: !!waNumber },
    });
    const fresh = await db.salesDocument.findUniqueOrThrow({ where: { id: t.id } });
    if (fresh.docType === 'invoice' && fresh.status === 'sent') {
      const { refreshInvoiceStatus } = await import('./documents.service.js');
      await db.$transaction((tx) => refreshInvoiceStatus(tx, fresh.id));
    }
    await scheduleDocumentReminders(db, fresh);
    t = {
      ...t,
      status: (await db.salesDocument.findUniqueOrThrow({ where: { id: t.id } })).status,
    };
  } else if (t.order) {
    const patch: Record<string, unknown> = {};
    if (!t.order.sentAt) patch.sentAt = now;
    if (t.order.status === 'approved') patch.status = 'sent';
    if (Object.keys(patch).length)
      await db.purchaseOrder.update({ where: { id: t.id }, data: patch });
    await logEvent(db, {
      companyId: t.companyId,
      entityType: 'purchase_order',
      entityId: t.id,
      type: 'sent',
      userId: auth.userId,
      note: input.channels.join(' + '),
    });
    t = {
      ...t,
      status: (await db.purchaseOrder.findUniqueOrThrow({ where: { id: t.id } })).status,
    };
  } else if (t.kind === 'payout') {
    await db.payoutReceipt.update({ where: { id: t.id }, data: { sentAt: now } });
    await logEvent(db, {
      companyId: t.companyId,
      entityType: 'payout',
      entityId: t.id,
      type: 'sent',
      userId: auth.userId,
      note: input.channels.join(' + '),
    });
  } else {
    await db.paymentReceipt.update({ where: { id: t.id }, data: { sentAt: now } });
    await logEvent(db, {
      companyId: t.companyId,
      entityType: 'receipt',
      entityId: t.id,
      type: 'sent',
      userId: auth.userId,
      note: input.channels.join(' + '),
    });
  }
  return { status: t.status, number: t.number, communications, whatsappUrl, shareUrl: link };
}

/** Optional automation (Settings, off by default): send the invoice when approved. */
export async function autoSendOnApproval(
  db: PrismaClient,
  config: Pick<Config, 'APP_URL'>,
  auth: AuthContext,
  doc: SalesDocument,
): Promise<void> {
  if (doc.docType !== 'invoice') return;
  const setting = await db.setting.findUnique({
    where: { companyId_key: { companyId: doc.companyId, key: 'autoSend' } },
  });
  if (!(setting?.value as { invoice?: boolean } | undefined)?.invoice) return;
  const preview = await sendPreview(db, auth, 'invoice', doc.id);
  if (preview.defaultEmails.length === 0) return;
  await sendDocument(db, config, auth, 'invoice', doc.id, {
    channels: ['email'],
    includeLink: true,
    email: {
      to: preview.defaultEmails,
      cc: [],
      bcc: [],
      subject: preview.email.subject,
      message: preview.email.body,
      attachPdf: true,
    },
  });
}

export async function autoSendReceipt(
  db: PrismaClient,
  config: Pick<Config, 'APP_URL'>,
  auth: AuthContext,
  receiptId: number,
): Promise<void> {
  const setting = await db.setting.findUnique({
    where: { companyId_key: { companyId: auth.companyId, key: 'autoSend' } },
  });
  if (!(setting?.value as { receipt?: boolean } | undefined)?.receipt) return;
  const preview = await sendPreview(db, auth, 'receipt', receiptId);
  if (preview.defaultEmails.length === 0) return;
  await sendDocument(db, config, auth, 'receipt', receiptId, {
    channels: ['email'],
    includeLink: true,
    email: {
      to: preview.defaultEmails,
      cc: [],
      bcc: [],
      subject: preview.email.subject,
      message: preview.email.body,
      attachPdf: true,
    },
  });
}
